import StatCards from './ui/StatCards';
import fetch from '../utils/authenticatedFetch';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../constants';
import './ReportsPage.css';

import {
  BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, ResponsiveContainer, CartesianGrid
} from 'recharts';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';

interface Application {
  id: number;
  researchTitle: string;
  researchDescription?: string;
  stageOneSubmittedAt: string;
  status: string | null;
  applicantId?: number;
  applicantName?: string;
  applicantSurname?: string;
  applicantFacultyCode?: string;
  applicantDepartmentCode?: string;
}
interface User {
  id: number;
  name: string;
  surname: string;
  userType: string;
  facultyCode?: string;
  departmentCode?: string;
}
interface Faculty { code: string; name: string; }
interface Department { departmentCode: string; name: string; facultyCode: string; }

const chartBlues = ['var(--chart-blue-1)','var(--chart-blue-2)','var(--chart-blue-3)','var(--chart-blue-4)','var(--chart-blue-3)','var(--chart-blue-4)','var(--chart-blue-3)','var(--chart-blue-1)'];
const statusColors: Record<string, string> = {
  SUBMITTED: 'var(--chart-blue-1)',
  APPROVED: 'var(--color-success)',
  ACCEPTED: 'var(--color-success)',
  DECLINED: 'var(--color-danger)',
  NOT_RECOMMENDED: 'var(--color-danger)',
  REJECTED: 'var(--color-danger)',
  RECOMMENDED: 'var(--chart-blue-2)',
  REVIEWED: 'var(--chart-blue-4)',
  SEND_FOR_REVIEW: 'var(--color-warning)',
  SEND_TO_FINAL: 'var(--color-success)',
  DRAFT: 'var(--text-secondary)',
};

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Recommended', value: 'RECOMMENDED' },
  { label: 'Send for Review', value: 'SEND_FOR_REVIEW' },
  { label: 'Reviewed', value: 'REVIEWED' },
  { label: 'Send to Final', value: 'SEND_TO_FINAL' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Declined', value: 'DECLINED' },
  { label: 'Revisions Required', value: 'NOT_RECOMMENDED' },
  { label: 'Draft', value: 'DRAFT' }
];

const progressStatuses = ['SUBMITTED', 'RECOMMENDED', 'SEND_FOR_REVIEW', 'REVIEWED', 'SEND_TO_FINAL', 'DRAFT'];

const statusLabels: Record<string, string> = {
  SUBMITTED: 'Submitted',
  RECOMMENDED: 'Recommended',
  SEND_FOR_REVIEW: 'Sent for Review',
  REVIEWED: 'Reviewed',
  SEND_TO_FINAL: 'Sent to Final',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  NOT_RECOMMENDED: 'Revisions Required',
  REJECTED: 'Not Recommended',
  DRAFT: 'Draft',
  APPROVED: 'Approved',
};

function normalizeStatus(status?: string | null): string {
  return (status || 'N/A').toUpperCase();
}

function formatStatus(status?: string | null): string {
  const key = normalizeStatus(status);
  return statusLabels[key] || key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function pct(value: number, total: number): string {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
}

const ReportsPage: React.FC = () => {
  const { account } = useAuth();
  const user = account as User | null;
  const userType = user?.userType || '';
  const navigate = useNavigate();

  // Data
  const [applications, setApplications] = useState<Application[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [dateFilter, setDateFilter] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // UI
  const [filtersOpen, setFiltersOpen] = useState(false); // collapsed on mobile

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
        setError('');
      try {
        const [facRes, depRes, appsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/v1/api/systems-settings/faculties/get-all`),
          fetch(`${BACKEND_URL}/v1/api/departments/all`),
          fetch(`${BACKEND_URL}/v1/api/research-applications`),
        ]);

        const facData = await facRes.json();
        const depData = await depRes.json();
        const appsData = await appsRes.json();

        setFaculties(Array.isArray(facData) ? facData : facData.data || []);
        setDepartments(Array.isArray(depData) ? depData : depData.data || []);

        let allApps: Application[] = appsData.success ? appsData.data : [];
        // Role scoping
        if (['ADMIN','RESEARCH_CHAIR','REVIEWER','RESEARCH_OFFICER','ADMIN_CYBER'].includes(userType)) {
          setApplications(allApps);
        } else if (userType === 'DEPARTMENT') {
          const deptCode = user?.departmentCode || '';
          setApplications(allApps.filter(a => a.applicantDepartmentCode === deptCode));
        } else if (['FACULTY_RAP','FACULTY_ADMIN','INSTITUTE_REPRESENTATIVE'].includes(userType)) {
          const facCode = user?.facultyCode || '';
          setApplications(allApps.filter(a => a.applicantFacultyCode === facCode));
        } else if (userType === 'APPLICANT') {
          setApplications(allApps.filter(a => a.applicantId === user?.id));
        } else {
          setApplications([]);
        }
      } catch {
        setError('Error fetching reports data');
      } finally {
        setLoading(false);
      }
    };
    if (userType) fetchAll();
  }, [userType, user]);

  // Derived: filtered apps
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];
    if (dateFilter) {
      filtered = filtered.filter(app => app.stageOneSubmittedAt.slice(0, 10) === dateFilter);
    }
    if (facultyFilter) {
      filtered = filtered.filter(app => (app.applicantFacultyCode || '') === facultyFilter);
    }
    if (departmentFilter) {
      filtered = filtered.filter(app => (app.applicantDepartmentCode || '') === departmentFilter);
    }
    if (statusFilter) {
      filtered = filtered.filter(app => (app.status || '').toUpperCase() === statusFilter.toUpperCase());
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(app =>
        app.researchTitle?.toLowerCase().includes(q) ||
        app.researchDescription?.toLowerCase().includes(q) ||
        `${app.applicantName || ''} ${app.applicantSurname || ''}`.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [applications, dateFilter, facultyFilter, departmentFilter, statusFilter, search]);

  // KPIs
  const kpi = useMemo(() => {
    const total = filteredApplications.length;
    const accepted = filteredApplications.filter(a => (a.status || '').toUpperCase() === 'ACCEPTED').length;
    const declined = filteredApplications.filter(a => {
      const status = (a.status || '').toUpperCase();
      return status === 'DECLINED' || status === 'NOT_RECOMMENDED' || status === 'REJECTED';
    }).length;
    const pending = filteredApplications.filter(a => {
      const s = (a.status || '').toUpperCase();
      return !['ACCEPTED','DECLINED','NOT_RECOMMENDED','REJECTED'].includes(s);
    }).length;
    const submitted = filteredApplications.filter(a => normalizeStatus(a.status) === 'SUBMITTED').length;
    const review = filteredApplications.filter(a => ['RECOMMENDED', 'SEND_FOR_REVIEW', 'REVIEWED', 'SEND_TO_FINAL'].includes(normalizeStatus(a.status))).length;
    const last30 = filteredApplications.filter(a => {
      const d = new Date(a.stageOneSubmittedAt);
      const days = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
      return Number.isFinite(days) && days <= 30;
    }).length;
    return {
      total,
      accepted,
      declined,
      pending,
      submitted,
      review,
      last30,
      
      declineRate: pct(declined, total),
      pendingRate: pct(pending, total),
    };
  }, [filteredApplications]);

  // Charts data
  const appsByMonthData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredApplications.forEach(a => {
      const d = new Date(a.stageOneSubmittedAt);
      const key = d.toLocaleString('default', { month: 'short', year: '2-digit' }); // e.g. "Jan 25"
      counts[key] = (counts[key] || 0) + 1;
    });
    const monthOrder: Record<string, number> = {
      Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11
    };
    return Object.entries(counts)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => {
        const [ma, ya] = a.month.split(' ');
        const [mb, yb] = b.month.split(' ');
        const yA = parseInt(ya, 10), yB = parseInt(yb, 10);
        if (yA !== yB) return yA - yB;
        return (monthOrder[ma] ?? 0) - (monthOrder[mb] ?? 0);
      });
  }, [filteredApplications]);

  const statusBreakdownData = useMemo(() => {
    const map: Record<string, number> = {};
    filteredApplications.forEach(a => {
      const s = (a.status || 'N/A').toUpperCase();
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({
      name: formatStatus(name),
      value,
      color: statusColors[name] || 'var(--text-secondary)'
    }));
  }, [filteredApplications]);

  const facultyVolumeData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredApplications.forEach(app => {
      const code = app.applicantFacultyCode || 'Unknown';
      const faculty = faculties.find(f => f.code === code);
      const label = faculty?.name || code;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filteredApplications, faculties]);

  const departmentVolumeData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredApplications.forEach(app => {
      const code = app.applicantDepartmentCode || 'Unknown';
      const department = departments.find(d => d.departmentCode === code);
      const label = department?.name || code;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filteredApplications, departments]);

  const insights = useMemo(() => {
    const peakMonth = appsByMonthData.reduce<{ month: string; count: number } | null>(
      (best, item) => (!best || item.count > best.count ? item : best),
      null
    );
    const topFaculty = facultyVolumeData[0];
    const topDepartment = departmentVolumeData[0];
    const statusCounts: Record<string, number> = {};
    filteredApplications.forEach(app => {
      const key = normalizeStatus(app.status);
      if (progressStatuses.includes(key)) {
        statusCounts[key] = (statusCounts[key] || 0) + 1;
      }
    });
    const bottleneck = Object.entries(statusCounts)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count)[0];

    return {
      peakMonth,
      topFaculty,
      topDepartment,
      bottleneck,
    };
  }, [appsByMonthData, facultyVolumeData, departmentVolumeData, filteredApplications]);

  // Exports
  const exportCSV = () => {
    const header = ['Applicant','Title','Status','Faculty','Department','Date Applied'];
    const rows = filteredApplications.map(a => [
      `"${`${a.applicantName || ''} ${a.applicantSurname || ''}`.trim().replace(/"/g,'""')}"`,
      `"${(a.researchTitle || '').replace(/"/g,'""')}"`,
      formatStatus(a.status),
      a.applicantFacultyCode || '',
      a.applicantDepartmentCode || '',
      new Date(a.stageOneSubmittedAt).toLocaleDateString()
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `applications_report_${Date.now()}.csv`);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Research Ethics Applications Report', 14, 16);
    doc.setFontSize(10);
    doc.text(`Total: ${kpi.total}  Accepted: ${kpi.accepted}  In Progress: ${kpi.pending}  Declined: ${kpi.declined}`, 14, 24);
    let y = 36;
    filteredApplications.slice(0, 70).forEach((a, i) => {
      const applicant = `${a.applicantName || ''} ${a.applicantSurname || ''}`.trim() || 'Unknown applicant';
      doc.text(`${i + 1}. ${applicant}`, 14, y);
      doc.text(`${a.researchTitle}`, 14, y + 6, { maxWidth: 180 });
      doc.text(`Status: ${formatStatus(a.status)} | Date: ${new Date(a.stageOneSubmittedAt).toLocaleDateString()}`, 14, y + 16);
      y += 26;
      if (y > 270) { doc.addPage(); y = 20; }
    });
    doc.save(`applications_report_${Date.now()}.pdf`);
  };

  const clearAll = () => {
    setDateFilter('');
    setFacultyFilter('');
    setDepartmentFilter('');
    setStatusFilter('');
    setSearch('');
  };

  return (
    <div className="reports-page">
      {/* Header */}
      <header className="reports-header">
        <div className="reports-title-wrap">
          <h1 className="reports-title">Analytics & Application Reports</h1>
          <p className="reports-sub">Track submissions, statuses, and trends at a glance.</p>
        </div>
        <div className="reports-actions">
          <button className="reports-export-btn" onClick={exportCSV} aria-label="Export CSV">Export CSV</button>
          <button className="reports-export-btn" onClick={exportPDF} aria-label="Export PDF">Export PDF</button>
          <button
            className="reports-mobile-filter-toggle"
            onClick={() => setFiltersOpen(v => !v)}
            aria-expanded={filtersOpen}
            aria-controls="reports-filters"
          >
            Filters
          </button>
        </div>
      </header>

      <hr className="reports-separator" />

      {/* KPI Cards */}
      <StatCards items={[
        {label:'Total applications',value:kpi.total,total:kpi.total,meterLabel:'Filtered portfolio',note:kpi.last30+' submitted in the last 30 days'},
        {label:'Accepted',value:kpi.accepted,total:kpi.total,meterLabel:'Of filtered applications',tone:'success'},
        {label:'Declined',value:kpi.declined,total:kpi.total,meterLabel:'Of filtered applications',tone:'danger',note:kpi.declineRate+' decline rate'},
        {label:'In progress',value:kpi.pending,total:kpi.total,meterLabel:'Of filtered applications',note:'Moving through ethics review'}
      ]}/>

      <section className="reports-insights" aria-label="Research ethics insights">
        <article className="insight-card primary">
          <span className="insight-kicker">Peak demand</span>
          <strong>{insights.peakMonth ? `${insights.peakMonth.month}: ${insights.peakMonth.count}` : 'No submissions yet'}</strong>
          <p>Highest monthly submission load in the current filtered view.</p>
        </article>
        <article className="insight-card">
          <span className="insight-kicker">Pipeline pressure</span>
          <strong>
            {insights.bottleneck ? `${formatStatus(insights.bottleneck.status)} (${insights.bottleneck.count})` : 'No active bottleneck'}
          </strong>
          <p>Stage with the highest number of active, unresolved applications.</p>
        </article>
        <article className="insight-card">
          <span className="insight-kicker">Top faculty</span>
          <strong>{insights.topFaculty ? `${insights.topFaculty.name} (${insights.topFaculty.count})` : 'No faculty data'}</strong>
          <p>Faculty contributing the largest share of applications.</p>
        </article>
        <article className="insight-card">
          <span className="insight-kicker">Top department</span>
          <strong>{insights.topDepartment ? `${insights.topDepartment.name} (${insights.topDepartment.count})` : 'No department data'}</strong>
          <p>Department with the highest ethics application volume.</p>
        </article>
      </section>

      {/* Filters */}
      <section
        id="reports-filters"
        className={`reports-filters ${filtersOpen ? 'open' : ''}`}
        aria-label="Filters"
      >
        <label>
          <span>Date</span>
          <input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
        </label>
        <label>
          <span>Faculty</span>
          <select value={facultyFilter} onChange={e => { setFacultyFilter(e.target.value); setDepartmentFilter(''); }}>
            <option value="">All</option>
            {faculties.map(f => (
              <option key={f.code} value={f.code}>{f.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Department</span>
          <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}>
            <option value="">All</option>
            {departments
              .filter(d => !facultyFilter || d.facultyCode === facultyFilter)
              .map(d => (
                <option key={d.departmentCode} value={d.departmentCode}>{d.name}</option>
              ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label className="reports-search">
          <span className="sr-only">Search</span>
          <input aria-label="Search title, applicant, or description"
            type="search"
            placeholder="Search title, applicant, or description"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </label>

        {(dateFilter || facultyFilter || departmentFilter || statusFilter || search) && (
          <button className="reports-clear-filter-btn" onClick={clearAll} aria-label="Clear filters">
            Clear
          </button>
        )}
      </section>

      {/* Charts */}
      <section className="reports-charts-row">
        <article className="reports-chart-box">
          <h3>Applications by Month</h3>
          {appsByMonthData.some(p=>p.count>0)?<ResponsiveContainer width="100%" height={260}>
            <BarChart data={appsByMonthData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: 10, color: "var(--text-primary)" }} labelStyle={{ color: "var(--text-primary)" }} />
              <Bar dataKey="count" radius={[6,6,0,0]}>
                {appsByMonthData.map((_, idx) => (
                  <Cell key={`bar-${idx}`} fill={chartBlues[idx % chartBlues.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>:<p className="ops-empty">No recorded data for this chart.</p>}
        </article>

        <article className="reports-chart-box">
          <h3>Status Breakdown</h3>
          {statusBreakdownData.some(p=>p.value>0)?<ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={statusBreakdownData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={3} stroke="var(--bg-surface)" strokeWidth={3} label>
                {statusBreakdownData.map((d, idx) => (
                  <Cell key={`pie-${idx}`} fill={d.color || chartBlues[idx % chartBlues.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: 10, color: "var(--text-primary)" }} labelStyle={{ color: "var(--text-primary)" }} />
            </PieChart>
          </ResponsiveContainer>:<p className="ops-empty">No recorded data for this chart.</p>}
        </article>
      </section>

      <section className="reports-charts-row reports-charts-row-secondary">
        <article className="reports-chart-box">
          <h3>Top Faculties by Application Volume</h3>
          {facultyVolumeData.some(p=>p.count>0)?<ResponsiveContainer width="100%" height={280}>
            <BarChart data={facultyVolumeData} layout="vertical" margin={{ top: 8, right: 20, left: 20, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: 10, color: "var(--text-primary)" }} labelStyle={{ color: "var(--text-primary)" }} />
              <Bar dataKey="count" fill="var(--chart-blue-2)" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>:<p className="ops-empty">No recorded data for this chart.</p>}
        </article>

        <article className="reports-chart-box">
          <h3>Top Departments by Application Volume</h3>
          {departmentVolumeData.some(p=>p.count>0)?<ResponsiveContainer width="100%" height={280}>
            <BarChart data={departmentVolumeData} layout="vertical" margin={{ top: 8, right: 20, left: 20, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: 10, color: "var(--text-primary)" }} labelStyle={{ color: "var(--text-primary)" }} />
              <Bar dataKey="count" fill="var(--chart-blue-1)" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>:<p className="ops-empty">No recorded data for this chart.</p>}
        </article>
      </section>

      {/* Table */}
      <h2 className="reports-subheading">Applications</h2>
      <div className="reports-app-table-container">
        {loading ? (
          <div className="reports-loading">
            <div className="sk-row" />
            <div className="sk-row" />
            <div className="sk-row" />
          </div>
        ) : error ? (
          <div className="reports-error">
            <p>{error}</p>
            <button className="reports-retry-btn" onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="reports-empty">
            <div className="empty-emoji" aria-hidden>📭</div>
            <p>No applications match your filters.</p>
            <button className="reports-clear-filter-btn" onClick={clearAll}>Clear filters</button>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="reports-app-table">
              <thead>
                <tr>
                  <th className="col-applicant">Applicant</th>
                  <th className="col-title">Title</th>
                  <th className="col-status">Status</th>
                  <th className="col-faculty">Faculty</th>
                  <th className="col-department">Department</th>
                  <th className="col-date">Date Applied</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.slice(0, 80).map(app => {
                  const statusKey = (app.status || '').toUpperCase();
                  const faculty = faculties.find(f => f.code === app.applicantFacultyCode);
                  const department = departments.find(d => d.departmentCode === app.applicantDepartmentCode);
                  return (
                    <tr key={app.id}>
                      <td className="col-applicant">
                        <strong>{`${app.applicantName || ''} ${app.applicantSurname || ''}`.trim() || 'Unknown applicant'}</strong>
                      </td>
                      <td className="col-title">{app.researchTitle}</td>
                      <td className="col-status">
                        <span
                          className={`badge ${statusKey ? `badge-${statusKey.toLowerCase()}` : ''}`}
                          style={{ color: statusColors[statusKey] || 'var(--chart-blue-1)' }}
                        >
                          {formatStatus(app.status)}
                        </span>
                      </td>
                      <td className="col-faculty">{faculty?.name || app.applicantFacultyCode || 'N/A'}</td>
                      <td className="col-department">{department?.name || app.applicantDepartmentCode || 'N/A'}</td>
                      <td className="col-date">{new Date(app.stageOneSubmittedAt).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="reports-view-all">
        <button className="reports-view-all-btn" onClick={() => navigate('/applications')}>
          View All Applications
        </button>
      </div>
    </div>
  );
};

export default ReportsPage;
